import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layers, Award, Lock } from "lucide-react";
import api from "@/lib/api";
import EmptyState from "@/components/EmptyState";

const AUDIENCE_LABEL = {
    public: "Public",
    partner: "Partner",
    internal: "Internal",
};

export default function Tracks() {
    const [tracks, setTracks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/tracks").then((r) => setTracks(r.data || [])).finally(() => setLoading(false));
    }, []);

    return (
        <div className="space-y-10" data-testid="tracks-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-3">Curriculum</div>
                    <h1 className="font-editorial text-4xl md:text-5xl text-ink-900 leading-[1.05]">Training tracks.</h1>
                    <p className="mt-3 text-base text-ink-500">Programs grouped by audience and intent.</p>
                </div>
                <div className="text-xs text-ink-500 font-mono uppercase tracking-[0.15em] hidden md:block" data-testid="tracks-count">
                    {tracks.length} {tracks.length === 1 ? "track" : "tracks"}
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-ink-500" data-testid="tracks-loading">Loading…</div>
            ) : tracks.length === 0 ? (
                <EmptyState
                    title="No tracks available yet"
                    description="Your administrator will publish tracks soon."
                    icon={Layers}
                    testId="tracks-empty"
                />
            ) : (
                <div className="grid md:grid-cols-2 gap-px bg-ink-300 border border-ink-300">
                    {tracks.map((t, idx) => {
                        const pubLocked = !t.is_published;
                        const inner = (
                            <div className={`group bg-white p-8 transition-colors flex flex-col h-full ${pubLocked ? "opacity-60" : "hover:bg-ink-100/60"}`}>
                                <div className="flex items-start justify-between mb-6">
                                    <div className="font-mono text-xs uppercase tracking-[0.2em] text-ink-400">
                                        {String(idx + 1).padStart(2, "0")}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {pubLocked && (
                                            <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-mono text-ink-500 bg-ink-100 border border-ink-200 px-2 py-1">
                                                <Lock size={10} strokeWidth={2} /> Coming soon
                                            </div>
                                        )}
                                        {t.certified && (
                                            <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-mono text-haevn-700 bg-haevn-50 border border-haevn-100 px-2 py-1">
                                                <Award size={10} strokeWidth={2} /> Certified
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <h3 className={`font-heading text-2xl text-ink-900 ${!pubLocked && "group-hover:text-haevn-700"} transition-colors`}>
                                    {t.title}
                                </h3>
                                <p className="text-sm text-ink-500 mt-3 leading-relaxed line-clamp-3">{t.description}</p>

                                <div className="mt-auto pt-8 flex items-center justify-between text-[11px] font-mono uppercase tracking-[0.15em] text-ink-400">
                                    <span>
                                        {t.completed_module_count ?? 0}/{t.module_count ?? 0} required
                                        {t.optional_module_count ? ` · +${t.optional_module_count} optional` : ""}
                                    </span>
                                    <span>{AUDIENCE_LABEL[t.audience] ?? t.audience}</span>
                                </div>
                            </div>
                        );

                        if (pubLocked) {
                            return (
                                <div key={t.slug} data-testid={`track-card-${t.slug}`} className="cursor-not-allowed">
                                    {inner}
                                </div>
                            );
                        }
                        return (
                            <Link
                                key={t.slug}
                                to={`/tracks/${t.slug}`}
                                data-testid={`track-card-${t.slug}`}
                                className="block"
                            >
                                {inner}
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
